import React from 'react';
import { Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { ExtractionProgress } from '@/modules/channels/services/extractionService';

interface ExtractionProgressBarProps {
  progress: ExtractionProgress | null;
  status: string | null;
  running: boolean;
}

interface StatusConfig {
  label: string;
  barPercent: number;
}

function resolveStatusConfig(
  status: string | null,
  progress: ExtractionProgress | null
): StatusConfig {
  if (!status) {
    return { label: 'Iniciando extracción...', barPercent: 0 };
  }

  switch (status) {
    case 'pending':
      return { label: 'Iniciando extracción...', barPercent: 2 };

    case 'fetching_chats':
      return { label: 'Obteniendo chats...', barPercent: 10 };

    case 'processing_messages': {
      const total = progress?.total_chats ?? 0;
      const processed = progress?.processed_chats ?? 0;
      const label =
        total > 0
          ? `Procesando mensajes (${processed}/${total} chats)`
          : 'Procesando mensajes...';
      const pct = total > 0 ? Math.round((processed / total) * 75) + 10 : 15;
      return { label, barPercent: Math.min(pct, 85) };
    }

    case 'processing_media': {
      const total = progress?.media_total ?? 0;
      const processed = progress?.media_processed ?? 0;
      const label =
        total > 0
          ? `Procesando multimedia (${processed}/${total})`
          : 'Procesando multimedia...';
      const pct = total > 0 ? Math.round((processed / total) * 10) + 85 : 88;
      return { label, barPercent: Math.min(pct, 95) };
    }

    case 'generating_markdown':
      return { label: 'Generando resumen...', barPercent: 97 };

    case 'completed':
      return { label: 'Extraccion completada', barPercent: 100 };

    case 'failed':
      return {
        label: progress?.error_message
          ? `Error: ${progress.error_message}`
          : 'Error en extraccion',
        barPercent: 0,
      };

    default:
      return { label: status, barPercent: 0 };
  }
}

export const ExtractionProgressBar: React.FC<ExtractionProgressBarProps> = ({
  progress,
  status,
  running,
}) => {
  const effectiveStatus = progress?.status ?? status;
  const { label, barPercent } = resolveStatusConfig(effectiveStatus, progress);

  const isCompleted = effectiveStatus === 'completed';
  const isFailed = effectiveStatus === 'failed';

  const containerClass = [
    'flex flex-col gap-1.5 rounded-lg px-3 py-2.5 border transition-colors duration-300',
    isCompleted
      ? 'bg-green-50 border-green-200'
      : isFailed
        ? 'bg-red-50 border-red-200'
        : 'bg-blue-50 border-blue-100',
  ].join(' ');

  const labelClass = [
    'text-xs font-medium truncate',
    isCompleted
      ? 'text-green-700'
      : isFailed
        ? 'text-red-700'
        : 'text-blue-700',
  ].join(' ');

  const trackClass = [
    'h-1.5 w-full rounded-full overflow-hidden',
    isCompleted ? 'bg-green-200' : isFailed ? 'bg-red-200' : 'bg-blue-200',
  ].join(' ');

  const fillClass = [
    'h-full rounded-full transition-all duration-500 ease-out',
    isCompleted ? 'bg-green-500' : isFailed ? 'bg-red-500' : 'bg-blue-500',
  ].join(' ');

  const renderIcon = () => {
    if (isCompleted) {
      return <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />;
    }
    if (isFailed) {
      return <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />;
    }
    if (running) {
      return <Loader2 className="w-4 h-4 text-blue-500 flex-shrink-0 animate-spin" />;
    }
    return <Loader2 className="w-4 h-4 text-blue-400 flex-shrink-0" />;
  };

  return (
    <div className={containerClass}>
      {/* Top row: icon + status text + percentage */}
      <div className="flex items-center gap-2">
        {renderIcon()}
        <span className={`${labelClass} flex-1`}>{label}</span>
        {!isFailed && (
          <span
            className={[
              'text-xs font-semibold tabular-nums flex-shrink-0',
              isCompleted ? 'text-green-600' : 'text-blue-600',
            ].join(' ')}
          >
            {barPercent}%
          </span>
        )}
      </div>

      {/* Progress bar */}
      {!isFailed && (
        <div className={trackClass}>
          <div
            className={fillClass}
            style={{ width: `${barPercent}%` }}
            role="progressbar"
            aria-valuenow={barPercent}
            aria-valuemin={0}
            aria-valuemax={100}
          />
        </div>
      )}
    </div>
  );
};
